import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useProject } from '../../context/ProjectContext';
import { useModelCatalog } from '../../hooks/useModelCatalog';

/**
 * SearchFilters - Advanced search filters component (SAT-109)
 *
 * Features:
 * - Project multi-select filter
 * - Date range picker (from/to)
 * - Model selection filter
 * - Clear all filters option
 * - Filter state persisted during session
 */

const SESSION_STORAGE_KEY = 'decisiv-performance-search-filters';

function SearchFilters({ onFiltersChange, isExpanded, onToggleExpanded }) {
  const { isDark } = useTheme();
  // DOS-415 Phase 4b: filter options follow the live catalog.
  const { models } = useModelCatalog();
  const AVAILABLE_MODELS = models.map((m) => ({ id: m.id, name: m.tierLabel || m.name }));
  const { projects } = useProject();

  // Filter state
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedModels, setSelectedModels] = useState([]);

  // Load filters from sessionStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setSelectedProjects(parsed.selectedProjects || []);
        setDateFrom(parsed.dateFrom || '');
        setDateTo(parsed.dateTo || '');
        setSelectedModels(parsed.selectedModels || []);
      }
    } catch (e) {
      console.error('Error loading search filters:', e);
    }
  }, []);

  // Save filters to sessionStorage and notify parent
  const saveFilters = useCallback((filters) => {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(filters));
    } catch (e) {
      console.error('Error saving search filters:', e);
    }
    onFiltersChange?.(filters);
  }, [onFiltersChange]);

  // Update filters when any value changes
  useEffect(() => {
    const filters = {
      selectedProjects,
      dateFrom,
      dateTo,
      selectedModels,
    };
    saveFilters(filters);
  }, [selectedProjects, dateFrom, dateTo, selectedModels, saveFilters]);

  // Handle project selection toggle
  const toggleProject = (projectId) => {
    setSelectedProjects(prev =>
      prev.includes(projectId)
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };

  // Handle model selection toggle
  const toggleModel = (modelId) => {
    setSelectedModels(prev =>
      prev.includes(modelId)
        ? prev.filter(id => id !== modelId)
        : [...prev, modelId]
    );
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedProjects([]);
    setDateFrom('');
    setDateTo('');
    setSelectedModels([]);
  };

  // Check if any filters are active
  const hasActiveFilters = selectedProjects.length > 0 || dateFrom || dateTo || selectedModels.length > 0;

  // Get active filter count
  const activeFilterCount = [
    selectedProjects.length > 0 ? 1 : 0,
    dateFrom || dateTo ? 1 : 0,
    selectedModels.length > 0 ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  return (
    <div className="relative">
      {/* Filter Toggle Button */}
      <button
        onClick={onToggleExpanded}
        className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded-md transition-colors ${
          isExpanded || hasActiveFilters
            ? 'bg-decisiv-blue/10 text-decisiv-blue'
            : isDark
              ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
        }`}
        title="Toggle search filters"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
        <span>Filters</span>
        {activeFilterCount > 0 && (
          <span className="flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-decisiv-blue text-white rounded-full">
            {activeFilterCount}
          </span>
        )}
        <svg
          className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Filter Panel */}
      {isExpanded && (
        <div className={`absolute left-0 right-0 mt-2 p-3 rounded-lg border shadow-lg z-50 ${
          isDark
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        }`}>
          {/* Header with Clear All */}
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
            <span className={`text-xs font-semibold uppercase tracking-wider ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Advanced Filters
            </span>
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="text-xs text-decisiv-blue hover:text-decisiv-blue/80 font-medium"
              >
                Clear all
              </button>
            )}
          </div>

          {/* Project Filter */}
          <div className="mb-3">
            <label className={`block text-xs font-medium mb-1.5 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Projects
            </label>
            <div className="flex flex-wrap gap-1.5">
              {projects && projects.length > 0 ? (
                projects.map(project => (
                  <button
                    key={project.id}
                    onClick={() => toggleProject(project.id)}
                    className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                      selectedProjects.includes(project.id)
                        ? 'bg-decisiv-blue text-white border-decisiv-blue'
                        : isDark
                          ? 'bg-gray-700 text-gray-300 border-gray-600 hover:border-gray-500'
                          : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {project.name}
                  </button>
                ))
              ) : (
                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  No projects available
                </span>
              )}
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="mb-3">
            <label className={`block text-xs font-medium mb-1.5 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Date Range
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  placeholder="From"
                  className={`w-full px-2 py-1.5 text-xs rounded-md border focus:outline-hidden focus:ring-1 focus:ring-decisiv-blue ${
                    isDark
                      ? 'bg-gray-700 text-white border-gray-600 placeholder-gray-500'
                      : 'bg-white text-gray-900 border-gray-200 placeholder-gray-400'
                  }`}
                />
              </div>
              <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>to</span>
              <div className="flex-1">
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  placeholder="To"
                  className={`w-full px-2 py-1.5 text-xs rounded-md border focus:outline-hidden focus:ring-1 focus:ring-decisiv-blue ${
                    isDark
                      ? 'bg-gray-700 text-white border-gray-600 placeholder-gray-500'
                      : 'bg-white text-gray-900 border-gray-200 placeholder-gray-400'
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Model Filter */}
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Models
            </label>
            <div className="flex flex-wrap gap-1.5">
              {AVAILABLE_MODELS.map(model => (
                <button
                  key={model.id}
                  onClick={() => toggleModel(model.id)}
                  className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                    selectedModels.includes(model.id)
                      ? 'bg-decisiv-blue text-white border-decisiv-blue'
                      : isDark
                        ? 'bg-gray-700 text-gray-300 border-gray-600 hover:border-gray-500'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {model.name}
                </button>
              ))}
            </div>
          </div>

          {/* Active Filters Summary */}
          {hasActiveFilters && (
            <div className={`mt-3 pt-2 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex flex-wrap gap-1">
                {selectedProjects.map(projectId => {
                  const project = projects?.find(p => p.id === projectId);
                  return project ? (
                    <span
                      key={projectId}
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded ${
                        isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {project.name}
                      <button
                        onClick={() => toggleProject(projectId)}
                        className="hover:text-red-500"
                      >
                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ) : null;
                })}
                {(dateFrom || dateTo) && (
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded ${
                    isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {dateFrom || 'Any'} - {dateTo || 'Any'}
                    <button
                      onClick={() => { setDateFrom(''); setDateTo(''); }}
                      className="hover:text-red-500"
                    >
                      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                )}
                {selectedModels.map(modelId => {
                  const model = AVAILABLE_MODELS.find(m => m.id === modelId);
                  return model ? (
                    <span
                      key={modelId}
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded ${
                        isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {model.name}
                      <button
                        onClick={() => toggleModel(modelId)}
                        className="hover:text-red-500"
                      >
                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchFilters;
